import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Form, Modal, Row, Spinner, Table } from 'react-bootstrap';
import {
  calculateTaskStatsByNcount,
  getAssessmentStatisticsContext,
  getAssessmentTypes,
  getCountries,
  getCountryScreenersByCountryId,
  updateAssessmentStatistics,
} from '../services/APIClient';

const DEFAULT_REQUIRED_COUNT = 5000;
const MAX_REQUIRED_COUNT = 100000;

const getErrorMessage = (error) =>
  error?.response?.data?.error?.message ||
  error?.response?.data?.message ||
  error?.message ||
  'The request failed';

const formatNumber = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  const number = Number(value);
  return Number.isFinite(number) ? number.toFixed(4) : '—';
};

const formatDate = (value) => {
  if (!value) return 'Not configured';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 'Not configured' : date.toLocaleDateString();
};

const getGradeLabel = (assessment) => {
  const grades = [1, 2, 3, 4].filter(
    (grade) =>
      assessment?.[`grade_level${grade}`] === true ||
      assessment?.[`gradeLevel${grade}`] === true,
  );
  return grades.length ? grades.join(', ') : 'Not configured';
};

function CalculateStandardisedScoresModal({ show, onHide }) {
  const [assessments, setAssessments] = useState([]);
  const [countries, setCountries] = useState([]);
  const [screenerDatesByAssessmentId, setScreenerDatesByAssessmentId] = useState({});
  const [countryFilter, setCountryFilter] = useState('');
  const [assessmentId, setAssessmentId] = useState('');
  const [assessmentContext, setAssessmentContext] = useState(null);
  const [requiredAnswerCount, setRequiredAnswerCount] = useState(
    String(DEFAULT_REQUIRED_COUNT),
  );
  const [previews, setPreviews] = useState([]);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [contextLoading, setContextLoading] = useState(false);
  const [checking, setChecking] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [error, setError] = useState('');
  const checkGeneration = useRef(0);
  const contextGeneration = useRef(0);

  useEffect(() => {
    checkGeneration.current += 1;
    setCountryFilter('');
    setAssessmentId('');
    setAssessmentContext(null);
    setRequiredAnswerCount(String(DEFAULT_REQUIRED_COUNT));
    setPreviews([]);
    setResult(null);
    setChecking(false);
    setShowConfirmation(false);
    setError('');
    setScreenerDatesByAssessmentId({});

    if (!show) {
      setLoading(false);
      setContextLoading(false);
      return;
    }

    let active = true;
    const loadOptions = async () => {
      setLoading(true);
      setError('');
      try {
        const [assessmentOptions, countryOptions] = await Promise.all([
          getAssessmentTypes(),
          getCountries(),
        ]);
        if (!active) return;
        const eligibleAssessments = assessmentOptions.filter(
          (assessment) => assessment.task !== true && assessment.rti !== true,
        );
        setAssessments(eligibleAssessments);
        setCountries(countryOptions);

        // CountryScreener.dateopened is only listable per-country, so fetch
        // every distinct country represented in the dropdown and merge.
        const distinctCountryIds = [
          ...new Set(eligibleAssessments.map((a) => a.countryid).filter(Boolean)),
        ];
        const screenerLists = await Promise.all(
          distinctCountryIds.map((countryId) =>
            getCountryScreenersByCountryId(countryId).catch(() => []),
          ),
        );
        if (!active) return;
        const dateByAssessmentId = {};
        screenerLists.flat().forEach((screener) => {
          if (screener.assessmentid) {
            dateByAssessmentId[screener.assessmentid] = screener.dateopened || null;
          }
        });
        setScreenerDatesByAssessmentId(dateByAssessmentId);
      } catch (loadError) {
        if (active) setError(getErrorMessage(loadError));
      } finally {
        if (active) setLoading(false);
      }
    };

    loadOptions();
    return () => {
      active = false;
    };
  }, [show]);

  useEffect(() => {
    contextGeneration.current += 1;
    const generation = contextGeneration.current;
    setAssessmentContext(null);
    setContextLoading(false);
    if (!show || !assessmentId) return;

    const selectedAssessment = assessments.find((item) => item.id === assessmentId);
    if (!selectedAssessment) return;

    setContextLoading(true);
    getAssessmentStatisticsContext(
      selectedAssessment.id,
      selectedAssessment.countryid,
    )
      .then((context) => {
        if (contextGeneration.current === generation) {
          setAssessmentContext(context);
          setError('');
        }
      })
      .catch((contextError) => {
        if (contextGeneration.current === generation) {
          setError(getErrorMessage(contextError));
        }
      })
      .finally(() => {
        if (contextGeneration.current === generation) setContextLoading(false);
      });
  }, [assessmentId, assessments, show]);

  const filteredAssessments = useMemo(() => {
    const base = countryFilter
      ? assessments.filter((assessment) => assessment.countryid === countryFilter)
      : assessments;
    // Order by CountryScreener.dateopened ascending; assessments with no
    // configured screener window sort last, in their original order.
    return [...base].sort((a, b) => {
      const dateA = screenerDatesByAssessmentId[a.id];
      const dateB = screenerDatesByAssessmentId[b.id];
      if (!dateA && !dateB) return 0;
      if (!dateA) return 1;
      if (!dateB) return -1;
      return new Date(dateA) - new Date(dateB);
    });
  }, [assessments, countryFilter, screenerDatesByAssessmentId]);
  const assessment = assessments.find((item) => item.id === assessmentId);
  const tasks = useMemo(() => {
    return (assessmentContext?.tasks || []).map((task) => ({
      id: task.taskId,
      name: task.taskName,
      order: task.order,
    }));
  }, [assessmentContext]);
  const academicYear = assessmentContext?.academicYear || '';
  const selectedCount = Number(requiredAnswerCount);
  const countIsValid =
    requiredAnswerCount !== '' &&
    Number.isInteger(selectedCount) &&
    selectedCount > 0 &&
    selectedCount <= MAX_REQUIRED_COUNT;
  const selectedCountLabel = countIsValid
    ? selectedCount.toLocaleString()
    : '—';
  const countryName =
    countries.find((country) => country.id === assessmentContext?.countryId)?.name ||
    assessmentContext?.countryId ||
    'Not configured';
  const previousPublish = assessmentContext?.publishedStatistics || null;
  const willReplacePrevious =
    previousPublish !== null && countIsValid && previousPublish.ncount !== selectedCount;
  const readyToCalculate =
    tasks.length > 0 &&
    assessmentContext?.canCalculate === true &&
    countIsValid &&
    previews.length === tasks.length &&
    previews.every(
      (preview) => !preview.error && preview.sampleCount === selectedCount,
    );

  const clearRunState = () => {
    checkGeneration.current += 1;
    setPreviews([]);
    setResult(null);
    setChecking(false);
    setError('');
    setShowConfirmation(false);
  };

  const handleCountryChange = (event) => {
    setCountryFilter(event.target.value);
    setAssessmentId('');
    clearRunState();
  };

  const handleAssessmentChange = (event) => {
    setAssessmentId(event.target.value);
    clearRunState();
  };

  const handleRequiredCountChange = (event) => {
    setRequiredAnswerCount(event.target.value);
    clearRunState();
  };

  const handleCheckCounts = async () => {
    if (
      !assessment ||
      !assessmentContext?.canCalculate ||
      !academicYear ||
      tasks.length === 0 ||
      !countIsValid
    ) return;

    const generation = checkGeneration.current + 1;
    checkGeneration.current = generation;
    setChecking(true);
    setError('');
    setResult(null);
    setPreviews([]);
    setShowConfirmation(false);
    const scope = {
      ncount: selectedCount,
      assessmentId: assessment.id,
      countryId: assessmentContext.countryId,
    };

    const taskPreviews = await Promise.all(
      tasks.map(async (task) => {
        try {
          const preview = await calculateTaskStatsByNcount({
            ...scope,
            taskId: task.id,
          });
          return { taskId: task.id, taskName: task.name, ...preview };
        } catch (previewError) {
          return {
            taskId: task.id,
            taskName: task.name,
            ncount: 0,
            sampleCount: 0,
            totalValidAnswers: 0,
            error: getErrorMessage(previewError),
          };
        }
      }),
    );

    if (
      checkGeneration.current === generation &&
      show &&
      assessmentId === assessment.id
    ) {
      setPreviews(taskPreviews);
      setChecking(false);
    }
  };

  const handleHide = () => {
    checkGeneration.current += 1;
    contextGeneration.current += 1;
    setCountryFilter('');
    setAssessmentId('');
    setAssessmentContext(null);
    setPreviews([]);
    setResult(null);
    setChecking(false);
    setContextLoading(false);
    setShowConfirmation(false);
    setError('');
    onHide();
  };

  const handleCalculate = async () => {
    if (!assessment || !assessmentContext?.canCalculate || calculating) return;

    setShowConfirmation(false);
    setCalculating(true);
    setError('');
    try {
      const publicationResult = await updateAssessmentStatistics({
        assessmentId: assessment.id,
        countryId: assessmentContext.countryId,
        ncount: selectedCount,
        category: 'general',
      });
      setResult(publicationResult);
      if (!publicationResult.success) {
        setError('Statistics were available, but one or more normalized-score recalculations failed. You can safely retry this assessment.');
      }
    } catch (calculationError) {
      setError(getErrorMessage(calculationError));
    } finally {
      setCalculating(false);
    }
  };

  return (
    <>
      <Modal show={show} onHide={calculating || checking ? undefined : handleHide} size="xl" centered>
        <Modal.Header closeButton={!calculating && !checking}>
          <Modal.Title>Calculate Standardised Scores</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="info">
            This manually publishes statistics only when every required task has {selectedCountLabel} valid answers within{' '}
            {assessmentContext?.dateOpened && assessmentContext?.dateClosed
              ? `${formatDate(assessmentContext.dateOpened)} – ${formatDate(assessmentContext.dateClosed)}`
              : 'the configured country screener window'}.
            {' '}Reporting visibility is controlled separately.
          </Alert>

          {error && <Alert variant="danger">{error}</Alert>}

          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" />
              <p className="mt-2 mb-0">Loading assessments…</p>
            </div>
          ) : (
            <>
              <Row className="g-3 mb-4">
                <Col md={3}>
                  <Form.Group controlId="statistics-country-filter">
                    <Form.Label>Filter by country</Form.Label>
                    <Form.Select value={countryFilter} onChange={handleCountryChange} disabled={checking || calculating}>
                      <option value="">All countries</option>
                      {countries.map((country) => (
                        <option key={country.id} value={country.id}>{country.name}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group controlId="statistics-assessment">
                    <Form.Label>Assessment</Form.Label>
                    <Form.Select value={assessmentId} onChange={handleAssessmentChange} disabled={checking || calculating}>
                      <option value="">Select an assessment…</option>
                      {filteredAssessments.map((option) => {
                        const screenerDate = screenerDatesByAssessmentId[option.id];
                        return (
                          <option key={option.id} value={option.id}>
                            {option.name}
                            {screenerDate ? ` — opens ${formatDate(screenerDate)}` : ''}
                          </option>
                        );
                      })}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group controlId="statistics-required-answer-count">
                    <Form.Label>Required answer count</Form.Label>
                    <Form.Control
                      type="number"
                      min="1"
                      max={MAX_REQUIRED_COUNT}
                      step="1"
                      value={requiredAnswerCount}
                      onChange={handleRequiredCountChange}
                      disabled={checking || calculating}
                      isInvalid={!countIsValid}
                    />
                    <Form.Control.Feedback type="invalid">
                      Enter a whole number from 1 to {MAX_REQUIRED_COUNT.toLocaleString()}.
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>

              {assessment && (
                <>
                  <Card className="mb-4">
                    <Card.Header>Country screener scope</Card.Header>
                    <Card.Body>
                      <Row className="g-3">
                        <Col md={3}><strong>Country</strong><div>{countryName}</div></Col>
                        <Col md={3}><strong>Grade(s)</strong><div>{getGradeLabel(assessmentContext)}</div></Col>
                        <Col md={3}><strong>Academic year</strong><div>{academicYear || 'Not available'}</div></Col>
                        <Col md={3}><strong>Statistics window</strong><div>{formatDate(assessmentContext?.dateOpened)} – {formatDate(assessmentContext?.dateClosed)}</div></Col>
                      </Row>
                      <Row className="g-3 mt-1">
                        <Col>
                          <strong>Previously published</strong>
                          <div>
                            {assessmentContext?.publishedStatistics
                              ? `${assessmentContext.publishedStatistics.ncount.toLocaleString()} answers per task, published ${formatDate(assessmentContext.publishedStatistics.publishedAt)}`
                              : 'Not yet published for this scope'}
                          </div>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>

                  {contextLoading ? (
                    <div className="text-center py-4"><Spinner animation="border" size="sm" /> Loading assessment scope…</div>
                  ) : assessmentContext?.blockedReason ? (
                    <Alert variant="warning">{assessmentContext.blockedReason}</Alert>
                  ) : null}

                  {!contextLoading && (tasks.length === 0 ? (
                    <Alert variant="warning">No required tasks are configured for this assessment.</Alert>
                  ) : (
                    <Table responsive bordered hover>
                      <thead>
                        <tr>
                          <th>Required task</th>
                          <th>Valid answers in window</th>
                          <th>Statistics sample</th>
                          <th>Mean</th>
                          <th>Population SD</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {tasks.map((task) => {
                          const preview = previews.find((item) => item.taskId === task.id);
                          return (
                            <tr key={task.id}>
                              <td>{task.name}</td>
                              <td>{preview ? preview.totalValidAnswers.toLocaleString() : '—'}</td>
                              <td>{preview ? `${preview.sampleCount.toLocaleString()} / ${selectedCountLabel}` : `— / ${selectedCountLabel}`}</td>
                              <td>{preview ? formatNumber(preview.mean) : '—'}</td>
                              <td>{preview ? formatNumber(preview.standardDeviation) : '—'}</td>
                              <td>
                                {preview?.error ? (
                                  <span className="text-danger">{preview.error}</span>
                                ) : preview?.sampleCount === selectedCount ? (
                                  <Badge bg="success">Ready</Badge>
                                ) : preview ? (
                                  <Badge bg="warning" text="dark">Below threshold</Badge>
                                ) : (
                                  <Badge bg="secondary">Not checked</Badge>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  ))}

                  {result && (
                    <Card className="mt-4">
                      <Card.Header className={result.success ? 'bg-success text-white' : 'bg-warning'}>
                        {result.success ? 'Calculation completed' : 'Calculation completed with failures'}
                      </Card.Header>
                      <Card.Body>
                        <p>
                          <strong>{result.assessmentName}</strong> · {result.academicYear} · {result.category} · {result.threshold.toLocaleString()} answers
                        </p>
                        <Table responsive bordered size="sm">
                          <thead>
                            <tr>
                              <th>Task</th>
                              <th>TaskStatistics</th>
                              <th>Archived</th>
                              <th>Updated</th>
                              <th>Created</th>
                              <th>Unchanged</th>
                              <th>Result</th>
                            </tr>
                          </thead>
                          <tbody>
                            {result.taskResults.map((taskResult) => (
                              <tr key={taskResult.taskId}>
                                <td>{taskResult.taskName}</td>
                                <td>
                                  {taskResult.statisticsStatus === 'inserted'
                                    ? 'Inserted'
                                    : taskResult.statisticsStatus === 'replaced'
                                    ? 'Replaced'
                                    : 'Already calculated (reused)'}
                                  <div className="small text-muted font-monospace">{taskResult.taskStatisticsId}</div>
                                </td>
                                <td>{taskResult.normalizedScoresArchived}</td>
                                <td>{taskResult.normalizedScoresUpdated}</td>
                                <td>{taskResult.normalizedScoresCreated}</td>
                                <td>{taskResult.normalizedScoresUnchanged}</td>
                                <td>{taskResult.success ? <Badge bg="success">Complete</Badge> : <span className="text-danger">{taskResult.message}</span>}</td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </Card.Body>
                    </Card>
                  )}
                </>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleHide} disabled={calculating || checking}>Close</Button>
          <Button variant="outline-primary" onClick={handleCheckCounts} disabled={!assessment || !assessmentContext?.canCalculate || tasks.length === 0 || !countIsValid || contextLoading || checking || calculating}>
            {checking ? <><Spinner animation="border" size="sm" className="me-2" />Checking…</> : 'Check Counts'}
          </Button>
          <Button variant="primary" onClick={() => setShowConfirmation(true)} disabled={!readyToCalculate || checking || calculating}>
            {calculating ? <><Spinner animation="border" size="sm" className="me-2" />Calculating…</> : 'Calculate Standardised Scores'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showConfirmation} onHide={() => setShowConfirmation(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm calculation</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Calculate and publish standardised-score statistics for <strong>{assessment?.name}</strong>?
          {' '}This will use {countIsValid ? selectedCountLabel : 'the selected number of'} valid answers per task.
          {' '}
          {willReplacePrevious ? (
            <>
              This will <strong>replace</strong> the statistics published on{' '}
              {formatDate(previousPublish.publishedAt)} using{' '}
              {previousPublish.ncount.toLocaleString()} answers per task. The previous
              statistics will be archived, not deleted, and standardised scores will be
              recalculated.{' '}
            </>
          ) : previousPublish ? (
            'Existing exact statistics will be reused. '
          ) : null}
          Reporting visibility will not change.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConfirmation(false)}>Cancel</Button>
          <Button variant="primary" onClick={handleCalculate}>Confirm calculation</Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default CalculateStandardisedScoresModal;
